"use client";

import { React, useState, useEffect } from 'react';
import './StepperClaim.css';
import { Header } from '../../../components';
import useSiniestrosStore from '../../../store/siniestrosStore';
import { getSinisterType, getToken, removeToken, setToken } from '../../../utils/claimManager';
import { Step0, Step1, Step2, Step3, Step4, Step5 } from '../index';
import { useStepper } from '../../../hooks/useStepper';
import StepperProgress from '../../../components/StepperProgress/StepperProgress';
import Footer from '../../../components/Footer/Footer';
import { showModal } from '../../../utils/functions';
import { shortUrl } from '../../../services/atmService';

const StepperClaim = () => {
  const {
    getConfig,
    config,
    getInstance,
    updateClaim,
    createClaim,
    claimToken,
    claimInstance,
    validateSinister,
    validateInsured,
    validateClaim,
    submitClaim,
    clearInstance
  } = useSiniestrosStore();
  const { stepper, goToStep, avanzar } = useStepper();
  const [isLoading, setLoading] = useState(false);
  const [validateData, setValidateData] = useState({
    things: '',
    branch: '',
    plate: '',
    dni: '',
    policy: '',
    sinister_date: '',
    sinister_number: ''
  });
  const [claim_number, setClaimNumber] = useState(null);
  const [urlStatus, setUrlStatus] = useState('');

  const resetAll = () => {
    setValidateData({
      things: '',
      branch: '',
      plate: '',
      dni: '',
      policy: '',
      sinister_date: '',
      sinister_number: ''
    });
    removeToken();
  }

  useEffect(() => {
    document.title = 'Terceros - ATM Seguros';
  }, [])

  useEffect(() => {
    setLoading(true)
    const isConfig = getConfig();
    if (isConfig) {
      setLoading(false);
    }
  }, [])

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        if (token) {
          await getInstance(token)
        } else {
          removeToken()
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [claimToken]);

  const safeAvanzar = async (stepNumber) => {
    const targetStep = stepNumber ?? stepper + 1;

    goToStep(targetStep);
    const token = getToken();

    if (token) {
      try {
        await updateClaim(token, { step: String(targetStep) });
      } catch (e) {
        console.error('Error actualizando el paso en el backend', e);
      }
    }
  };


  const safeRetroceder = async (stepNumber) => {
    const targetStep = stepNumber ?? stepper - 1;

    const token = getToken();
    if (token) {
      try {
        await updateClaim(token, { step: String(targetStep) });
        //await getInstance(token)
        goToStep(targetStep);
      } catch (e) {
        console.error('Error retrocediendo paso en backend', e);
      }
    } else {
      goToStep(targetStep);
    }
  };

  const handleUpdateClaim = async (token, data) => {
    setLoading(true);
    try {
      const res = await updateClaim(token, data);
      if (res.status !== 200 && res.status !== 201) {
        return false;
      } else {
        return true;
      }
    } catch (e) {
      await showModal('Algunos datos no están completos', 'Ocurrió un error al actualizar los datos del reclamo, por favor revisá que los campos estén completos.', 'warning');
      return false;

    } finally {
      setLoading(false);
    }
  };


  const fetchSinisterNumber = async (data) => {
    setLoading(true);

    try {
      const token = getToken();
      const res = await validateSinister(data, token);

      if (res?.status !== 200 && res?.status !== 201) {
        await showModal('¡UPS! Ocurrió un error al obtener el número de siniestro', res?.data || res?.status, 'error');
        return false;
      }

      const things = getSinisterType();
      const updated = await updateClaim(token, { things });

      if (updated?.status === 200 || updated?.status === 201) {
        return true;
      } else {
        await showModal('¡UPS! Ocurrió un error al actualizar los datos del reclamo', '', 'error');
        return false;
      }

    } catch (e) {
      console.error('Error completo:', e);
      await showModal('¡UPS! No hay coincidencias', e?.response?.data || 'Error desconocido', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // TODO: revisar manejo de errores
  const fetchSinisterData = async (data) => {
    setLoading(true);
    try {
      const token = getToken();
      if (token) {
        const res = await validateInsured(data, token);

        if (res?.status !== 200 && res?.status !== 201) {
          await showModal('Error en la validación', res?.data || res?.status, 'warning');
          return false;
        }

        if (res?.data[0]?.siniestro === null) {
          showModal('Denuncia de siniestro no registrada', 'No se registró ninguna denuncia de siniestro con la información proporcionada. Por favor, volvé a intentarlo en unos días.', 'error');
          return false;
        }

        return true;
      }
    } catch (e) {
      console.error('Error completo:', e);

      if (e.status >= 500)
        await showModal('¡UPS! Datos incorrectos', e?.response?.data || 'Ocurrió un error inesperado, intente nuevamente más tarde o pruebe ingresando otros datos', 'warning');
      else
        await showModal('Denuncia de siniestro no registrada', 'No se registró ninguna denuncia de siniestro con la información proporcionada. Por favor, volvé a intentarlo en unos días.', 'error');

      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClaim = async () => {
    setLoading(true);
    try {
      const token = await createClaim();
      if (token) {
        setToken(token);
        const things = getSinisterType();
        const updated = await updateClaim(token, { things: things });

        if (updated.status === 200 || updated.status === 201) {
          return true;
        } else {
          await showModal('¡UPS! Ocurrió un error al actualizar los datos del reclamo', '', 'error');
        }
      }
    } catch (e) {
      console.error(e);
      await showModal('¡UPS! Ocurrió un error al originar el reclamo', 'Ocurrió un error al momento de generar un nuevo reclamo, intentá nuevamente más tarde', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateClaim = async (token) => {
    setLoading(true);
    try {
      const state = await validateClaim(token);
      return state;
    } catch (e) {
      console.error(e);
      return e;
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitClaim = async (token) => {
    setLoading(true);
    try {
      const number = await submitClaim(token);
      if (number) {
        setClaimNumber(number);

        const response = await shortUrl(`${window.location.origin}/sinister-status?id=${token}`);
        if (response) {
          setUrlStatus(response);
        }
      }
      return number;
    } catch (e) {
      console.error(e);
      return e;
    } finally {
      setLoading(false)
    }
  }


  const handleStepper = () => {
    const commonProps = {
      avanzar: safeAvanzar,
      retroceder: safeRetroceder,
      instance: claimInstance,
      getInstance: getInstance,
      clearInstance: clearInstance,
      loader: isLoading,
      config: config,

    };

    switch (stepper) {
      case 0:
        return <Step0 {...commonProps}
          formData={validateData}
          setFormData={setValidateData}
          createStep={handleCreateClaim}
          onSuccessStep={safeAvanzar}
        />;
      case 1:
        return <Step1
          {...commonProps}
          formData={validateData}
          setFormData={setValidateData}
          config={config}
        />;
      case 2:
        return <Step2
          {...commonProps}
          formData={validateData}
          setFormData={setValidateData}
          fetchSinisterData={fetchSinisterData}
          fetchSinisterNumber={fetchSinisterNumber}
          updateClaim={handleUpdateClaim}
          goToStep={safeAvanzar}
        />;
      case 3:
      case 3.2:
        return <Step3
          {...commonProps}
          handleUpdateClaim={handleUpdateClaim} />;
      case 4:
      case 4.2:
        return <Step4
          {...commonProps}
          handleUpdateClaim={handleUpdateClaim}
          lastAvanzar={avanzar}
          validateClaim={handleValidateClaim}
          submitClaim={handleSubmitClaim} />;
      case 5:
        return <Step5
          {...commonProps}
          resetAll={resetAll}
          claimNumber={claim_number}
          urlStatus={urlStatus}
        />;
      default:
        return <Step0
          {...commonProps}
          fetchSinisterNumber={fetchSinisterNumber}
          config={config}
        />;
    }
  };

  return (
    <div className='stepperclaim md:pb-24'>
      <Header />
      {
        process.env.REACT_APP_ENVIROMENT === 'dev' ?
          <div className='w-full justify-center items-center flex'>
            <p className='text-md text-gray-500 mx-auto border-2 border-red text-center w-auto p-2'>
              Id de siniestro: {claimInstance?.uuid} <br />
              Incluir en fotos el número de siniestro.
            </p>
          </div>
          :
          <></>
      }
      <StepperProgress currentStep={stepper} />
      {handleStepper()}
      <Footer />
    </div>
  );
};

export default StepperClaim;